import React, { useState, useEffect, useRef } from 'react';
import { FiChevronDown, FiChevronRight, FiSave, FiCheck, FiX, FiSkipForward } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import programService from '../../services/programService';
import SessionBuilder from './SessionBuilder';
import CycleRepeater from './CycleRepeater';
import ProgramMetadataEditor from './ProgramMetadataEditor';
import ClientAssignmentModal from './ClientAssignmentModal';
import './ProgramWizard.css';

const ProgramWizard = ({ programId: initialProgramId = null, onClose }) => {
  const { currentUser, userProfile } = useAuth();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const lastSavedStateRef = useRef(null);

  // Track the program ID (can be updated after first save)
  const [programId, setProgramId] = useState(initialProgramId);

  // Program state
  const [program, setProgram] = useState({
    status: 'draft', // 'draft' or 'complete'
    name: '',
    sessionsPerCycle: null,
    daysPerCycle: null,
    numberOfCycles: null,
    groupType: '', // 'strength', 'cardio', 'hybrid', 'rehab'
    cycles: [], // Array of cycles, each containing sessions
    metadata: {
      description: '',
      category: 'general_fitness',
      difficulty: 'intermediate',
      targetClient: null,
      goals: [],
      equipment: [],
      tags: [],
      notes: ''
    }
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [expandedStep, setExpandedStep] = useState(1);
  const [currentCycleIndex, setCurrentCycleIndex] = useState(0);
  const [currentSessionIndex, setCurrentSessionIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showClientAssignment, setShowClientAssignment] = useState(false);
  const [completedProgramData, setCompletedProgramData] = useState(null);

  // Load existing program if editing
  useEffect(() => {
    if (programId) {
      loadProgram();
    }
  }, [programId]);

  // Track unsaved changes
  useEffect(() => {
    if (lastSavedStateRef.current) {
      const hasChanges = JSON.stringify(program) !== JSON.stringify(lastSavedStateRef.current);
      setHasUnsavedChanges(hasChanges);
    }
  }, [program]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadProgram = async () => {
    try {
      const loadedProgram = await programService.getProgram(programId);
      if (loadedProgram) {
        setProgram(loadedProgram);
        lastSavedStateRef.current = loadedProgram;
        // Set completed steps based on loaded data
        const completed = new Set();
        if (loadedProgram.name) completed.add(1);
        if (loadedProgram.sessionsPerCycle) completed.add(2);
        if (loadedProgram.daysPerCycle && loadedProgram.numberOfCycles) completed.add(3);
        if (loadedProgram.groupType) completed.add(4);
        setCompletedSteps(completed);
      }
    } catch (error) {
      console.error('[ProgramWizard] Error loading program:', error);
      alert('Error loading program');
    }
  };

  const saveDraft = async (silent = false) => {
    if (!program.name) {
      if (!silent) alert('Please enter a program name before saving');
      return;
    }

    setSaving(true);
    try {
      let savedProgram;
      const creatorName = userProfile?.displayName || userProfile?.email || 'Unknown Coach';

      if (programId) {
        await programService.updateProgram(programId, { ...program, status: 'draft' });
        savedProgram = { id: programId, ...program };
      } else {
        savedProgram = await programService.createProgram(currentUser.uid, { ...program, status: 'draft' }, creatorName);
        // Update programId state after first save
        if (savedProgram.id) {
          setProgramId(savedProgram.id);
        }
      }

      lastSavedStateRef.current = savedProgram;
      setHasUnsavedChanges(false);
      if (!silent) alert('Draft saved successfully!');
      return savedProgram;
    } catch (error) {
      console.error('[ProgramWizard] Error saving draft:', error);
      if (!silent) alert('Error saving draft. Please try again.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const completeProgram = async () => {
    if (!validateProgram()) {
      return;
    }

    setSaving(true);
    try {
      let savedProgram;
      const creatorName = userProfile?.displayName || userProfile?.email || 'Unknown Coach';

      if (programId) {
        await programService.updateProgram(programId, { ...program, status: 'complete' });
        savedProgram = { id: programId, ...program };
      } else {
        savedProgram = await programService.createProgram(currentUser.uid, { ...program, status: 'complete' }, creatorName);
        // Update programId state after first save
        if (savedProgram.id) {
          setProgramId(savedProgram.id);
        }
      }

      lastSavedStateRef.current = savedProgram;
      setHasUnsavedChanges(false);
      alert('Program completed successfully!');

      // Ask about client assignment
      if (window.confirm('Would you like to assign this program to a client now?')) {
        setCompletedProgramData(savedProgram);
        setShowClientAssignment(true);
      } else if (onClose) {
        onClose(savedProgram);
      }
    } catch (error) {
      console.error('[ProgramWizard] Error completing program:', error);
      alert('Error completing program. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const validateProgram = () => {
    if (!program.name) {
      alert('Please enter a program name');
      return false;
    }
    if (!program.sessionsPerCycle || !program.daysPerCycle || !program.numberOfCycles) {
      alert('Please complete program structure (steps 2-3)');
      return false;
    }
    if (!program.groupType) {
      alert('Please select a program type (step 4)');
      return false;
    }

    // Check if all cycles and sessions are created
    const expectedCycles = program.numberOfCycles;
    const expectedSessionsPerCycle = program.sessionsPerCycle;

    if (program.cycles.length < expectedCycles) {
      alert(`Please complete all ${expectedCycles} cycles (currently ${program.cycles.length} completed)`);
      return false;
    }

    for (let i = 0; i < program.cycles.length; i++) {
      const cycle = program.cycles[i];
      if (!cycle.sessions || cycle.sessions.length < expectedSessionsPerCycle) {
        alert(`Cycle ${i + 1} is incomplete (needs ${expectedSessionsPerCycle} sessions)`);
        return false;
      }
    }

    return true;
  };

  const handleCloseWithWarning = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Do you want to leave without saving?')) {
        if (onClose) onClose();
      }
    } else {
      if (onClose) onClose();
    }
  };

  const toggleStep = (stepNumber) => {
    setExpandedStep(expandedStep === stepNumber ? null : stepNumber);
  };

  const completeStep = async (stepNumber) => {
    setCompletedSteps(prev => new Set([...prev, stepNumber]));
    // Auto-expand next step
    if (stepNumber < 4) {
      setExpandedStep(stepNumber + 1);
    }
  };

  const skipStep = (stepNumber) => {
    setExpandedStep(stepNumber + 1);
  };

  // Handle client assignment
  const handleClientAssignment = async (targetClient) => {
    try {
      // Update program metadata with assigned client
      await programService.updateProgram(completedProgramData.id, {
        'metadata.targetClient': targetClient
      });

      setShowClientAssignment(false);
      alert(`Program assigned to ${targetClient.clientName} successfully!`);

      if (onClose) {
        onClose(completedProgramData);
      }
    } catch (error) {
      console.error('[ProgramWizard] Error assigning program to client:', error);
      alert('Error assigning program. Please try again.');
    }
  };

  // Step 1: Program Name
  const handleNameComplete = async () => {
    if (!program.name.trim()) {
      alert('Please enter a program name');
      return;
    }
    // Save immediately when Next is clicked
    await saveDraft(true);
    await completeStep(1);
  };

  // Step 2: Sessions per cycle
  const handleSessionsPerCycleComplete = async () => {
    if (!program.sessionsPerCycle || program.sessionsPerCycle < 1) {
      alert('Please enter number of sessions per cycle');
      return;
    }
    // Save immediately when Next is clicked
    await saveDraft(true);
    await completeStep(2);
  };

  // Step 3: Cycle structure
  const handleCycleStructureComplete = async () => {
    if (!program.daysPerCycle || program.daysPerCycle < 1) {
      alert('Please enter days per cycle');
      return;
    }
    if (!program.numberOfCycles || program.numberOfCycles < 1) {
      alert('Please enter number of cycles');
      return;
    }

    // Initialize cycles array if needed
    if (!program.cycles || program.cycles.length === 0) {
      const cycles = Array.from({ length: program.numberOfCycles }, (_, i) => ({
        cycleNumber: i + 1,
        sessions: []
      }));
      setProgram(prev => ({ ...prev, cycles }));

      // Wait for state to update before saving
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Save immediately when Next is clicked
    await saveDraft(true);
    await completeStep(3);
  };

  // Step 4: Group type
  const handleGroupTypeComplete = async () => {
    if (!program.groupType) {
      alert('Please select a program type');
      return;
    }
    // Save immediately when Next is clicked
    await saveDraft(true);
    await completeStep(4);
    setExpandedStep(5); // Move to session creation
  };

  return (
    <div className="program-wizard">
      {/* Header */}
      <div className="wizard-header">
        <div className="wizard-header-left">
          <h2>{programId ? 'Edit Program' : 'Create New Program'}</h2>
          {program.status === 'draft' && <span className="draft-badge">Draft</span>}
          {hasUnsavedChanges && <span className="unsaved-indicator">• Unsaved changes</span>}
        </div>
        <div className="wizard-header-actions">
          <button
            className="save-draft-btn"
            onClick={saveDraft}
            disabled={saving}
          >
            <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            className="close-wizard-btn"
            onClick={handleCloseWithWarning}
          >
            <FiX />
          </button>
        </div>
      </div>

      {/* Wizard Steps */}
      <div className="wizard-content">
        {/* Step 1: Program Name */}
        <div className={`wizard-step ${completedSteps.has(1) ? 'completed' : ''}`}>
          <div className="step-header" onClick={() => toggleStep(1)}>
            <div className="step-header-left">
              {completedSteps.has(1) ? <FiCheck className="step-icon completed" /> : <span className="step-number">1</span>}
              <h3>Program Name</h3>
            </div>
            {expandedStep === 1 ? <FiChevronDown /> : <FiChevronRight />}
          </div>
          {expandedStep === 1 && (
            <div className="step-content">
              <input
                type="text"
                className="program-name-input"
                placeholder="Enter program name (e.g., 8-Week Strength Builder)"
                value={program.name}
                onChange={(e) => setProgram(prev => ({ ...prev, name: e.target.value }))}
              />
              <div className="step-actions">
                <button className="next-btn" onClick={handleNameComplete}>
                  Next <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Sessions per Cycle */}
        <div className={`wizard-step ${completedSteps.has(2) ? 'completed' : ''}`}>
          <div className="step-header" onClick={() => toggleStep(2)}>
            <div className="step-header-left">
              {completedSteps.has(2) ? <FiCheck className="step-icon completed" /> : <span className="step-number">2</span>}
              <h3>Workout Sessions per Cycle</h3>
            </div>
            {expandedStep === 2 ? <FiChevronDown /> : <FiChevronRight />}
          </div>
          {expandedStep === 2 && (
            <div className="step-content">
              <label>How many workout sessions per cycle?</label>
              <input
                type="number"
                min="1"
                max="10"
                value={program.sessionsPerCycle || ''}
                onChange={(e) => setProgram(prev => ({ ...prev, sessionsPerCycle: parseInt(e.target.value) }))}
                placeholder="e.g., 3 for 3 sessions per cycle"
              />
              <p className="step-hint">Example: 3 sessions = Session 1, Session 2, Session 3</p>
              <div className="step-actions">
                <button className="next-btn" onClick={handleSessionsPerCycleComplete}>
                  Next <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Step 3: Cycle Structure */}
        <div className={`wizard-step ${completedSteps.has(3) ? 'completed' : ''}`}>
          <div className="step-header" onClick={() => toggleStep(3)}>
            <div className="step-header-left">
              {completedSteps.has(3) ? <FiCheck className="step-icon completed" /> : <span className="step-number">3</span>}
              <h3>Define Cycles</h3>
            </div>
            {expandedStep === 3 ? <FiChevronDown /> : <FiChevronRight />}
          </div>
          {expandedStep === 3 && (
            <div className="step-content">
              <div className="cycle-inputs">
                <div className="form-group">
                  <label>Days per Cycle</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={program.daysPerCycle || ''}
                    onChange={(e) => setProgram(prev => ({ ...prev, daysPerCycle: parseInt(e.target.value) }))}
                    placeholder="e.g., 7 for weekly, 10 for 10-day cycle"
                  />
                </div>
                <div className="form-group">
                  <label>Number of Cycles</label>
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={program.numberOfCycles || ''}
                    onChange={(e) => setProgram(prev => ({ ...prev, numberOfCycles: parseInt(e.target.value) }))}
                    placeholder="e.g., 4 for 4 cycles"
                  />
                </div>
              </div>
              <p className="step-hint">
                Example: 7 days per cycle × 4 cycles = 4-week program with {program.sessionsPerCycle || 'X'} sessions per week
              </p>
              <div className="step-actions">
                <button className="next-btn" onClick={handleCycleStructureComplete}>
                  Next <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Step 4: Group Type */}
        <div className={`wizard-step ${completedSteps.has(4) ? 'completed' : ''}`}>
          <div className="step-header" onClick={() => toggleStep(4)}>
            <div className="step-header-left">
              {completedSteps.has(4) ? <FiCheck className="step-icon completed" /> : <span className="step-number">4</span>}
              <h3>Program Type</h3>
            </div>
            {expandedStep === 4 ? <FiChevronDown /> : <FiChevronRight />}
          </div>
          {expandedStep === 4 && (
            <div className="step-content">
              <div className="group-type-options">
                {['strength', 'cardio', 'hybrid', 'rehab'].map(type => (
                  <div
                    key={type}
                    className={`group-type-card ${program.groupType === type ? 'selected' : ''}`}
                    onClick={() => setProgram(prev => ({ ...prev, groupType: type }))}
                  >
                    <h4>{type.charAt(0).toUpperCase() + type.slice(1)}</h4>
                  </div>
                ))}
              </div>
              <div className="step-actions">
                <button className="next-btn" onClick={handleGroupTypeComplete}>
                  Next <FiChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Step 5+: Session Creation */}
        {completedSteps.has(4) && program.cycles.length > 0 && (
          <SessionBuilder
            program={program}
            setProgram={setProgram}
            currentCycleIndex={currentCycleIndex}
            setCurrentCycleIndex={setCurrentCycleIndex}
            currentSessionIndex={currentSessionIndex}
            setCurrentSessionIndex={setCurrentSessionIndex}
            onAutoSave={() => saveDraft(true)}
          />
        )}
      </div>

      {/* Footer */}
      <div className="wizard-footer">
        <button
          className="save-draft-btn"
          onClick={saveDraft}
          disabled={saving}
        >
          <FiSave /> {saving ? 'Saving...' : 'Save Draft'}
        </button>
        {completedSteps.has(4) && (
          <button
            className="complete-program-btn"
            onClick={completeProgram}
            disabled={saving}
          >
            <FiCheck /> Complete Program
          </button>
        )}
      </div>

      {/* Client Assignment Modal */}
      {showClientAssignment && completedProgramData && (
        <ClientAssignmentModal
          program={completedProgramData}
          onAssign={handleClientAssignment}
          onClose={() => {
            setShowClientAssignment(false);
            if (onClose) {
              onClose(completedProgramData);
            }
          }}
        />
      )}
    </div>
  );
};

export default ProgramWizard;
